import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from "../../hooks/useTranslation";
import { V2, pickLang } from "../copy";
import { Arrow, Display, Eyebrow } from "./Primitives";
import { route } from "../paths";

// Order mirrors `services.items` in the translations (index → copy).
// protection-scene.png / finition.png are the two halves of protection.png.
const ITEMS = [
  { id: "nettoyage-professionnel", img: "/illustrations/landing/nettoyage.webp", option: false },
  { id: "collecte-livraison", img: "/illustrations/landing/collecte.webp", option: true },
  { id: "service-express", img: "/illustrations/landing/livraison.webp", option: true },
  { id: "protection-premium", img: "/illustrations/landing/protection-scene.webp", option: true },
  { id: "finition-luxe", img: "/illustrations/landing/finition.webp", option: false },
  { id: "entreprises", img: "/illustrations/landing/entreprises.webp", option: false },
];

/**
 * Editorial service index: big serif rows, a preview image that floats after
 * the cursor on desktop, inline thumbnails on touch devices.
 */
const ServicesV2: React.FC<{ hideHeader?: boolean }> = ({ hideHeader = false }) => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const lang = pickLang(currentLanguage);
  const c = V2[lang].services;

  const [hover, setHover] = useState<number | null>(null);
  const floatRef = useRef<HTMLDivElement>(null);

  // Position from any mouse event — including the `mouseenter` that fires
  // when a row scrolls under a resting cursor — so the preview never pops
  // in at its default (0,0) before the first `mousemove`.
  const place = (e: React.MouseEvent) => {
    const el = floatRef.current;
    if (!el) return;
    el.style.left = `${e.clientX}px`;
    el.style.top = `${e.clientY}px`;
  };
  const enter = (i: number) => (e: React.MouseEvent) => {
    place(e);
    setHover(i);
  };

  const href = (id: string) =>
    id === "entreprises" ? route(lang, "entreprises") : route(lang, `service/${id}`);

  return (
    <section id="v2-services" className={`bg-bone text-ink ${hideHeader ? "py-16 sm:py-24" : "py-24 sm:py-32"}`}>
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        {!hideHeader && (
        <div className="grid gap-6 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Eyebrow index="03">{c.label}</Eyebrow>
            <Display className="mt-5 text-[clamp(2.6rem,7vw,6.5rem)]">
              {c.title}
            </Display>
          </div>
          <p
            className="v2-reveal self-end text-ink/60 lg:col-span-6 lg:col-start-7"
            data-delay="2"
          >
            {c.subtitle}
          </p>
        </div>
        )}

        <div
          className={`v2-reveal ${hideHeader ? "" : "mt-14"}`}
          data-delay="2"
          onMouseMove={place}
          onMouseLeave={() => setHover(null)}
        >
          {ITEMS.map((it, i) => (
            <Link
              key={it.id}
              to={href(it.id)}
              onMouseEnter={enter(i)}
              className={`v2-row group grid grid-cols-[2.5rem_1fr_auto] items-center gap-4 border-t border-ink/15 py-6 sm:grid-cols-[4rem_1fr_auto] sm:gap-8 sm:py-8 ${
                i === ITEMS.length - 1 ? "border-b" : ""
              }`}
            >
              <span className="font-mono text-[11px] tracking-[0.22em] text-ink/40">
                {`0${i + 1}`}
              </span>
              <span className="min-w-0">
                <span className="v2-row-title block font-display text-[clamp(1.75rem,4.6vw,4.25rem)] leading-[0.98] tracking-[-0.02em]">
                  {t(`services.items.${i}.title`)}
                  {it.option && (
                    <span className="ml-3 inline-block -translate-y-[0.35em] rounded-full border border-teal-deep px-2 py-0.5 align-middle font-mono text-[9px] uppercase tracking-[0.2em] text-teal-deep sm:ml-4 sm:text-[10px]">
                      {lang === "fr" ? "Option" : "Add-on"}
                    </span>
                  )}
                </span>
                <span className="mt-2 hidden max-w-xl text-sm text-ink/55 sm:block">
                  {t(`services.items.${i}.description`)}
                </span>
              </span>
              <span className="flex items-center gap-4">
                <img
                  src={it.img}
                  alt=""
                  loading="lazy"
                  className="h-14 w-14 rounded-[4px] bg-white object-cover sm:hidden"
                />
                <span className="hidden h-12 w-12 items-center justify-center rounded-full border border-ink/20 transition-all duration-500 group-hover:border-teal group-hover:bg-teal group-hover:text-white sm:flex">
                  <Arrow />
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* floating preview (desktop) */}
      <div
        ref={floatRef}
        className={`v2-float hidden bg-white lg:block ${hover !== null ? "is-on" : ""}`}
        aria-hidden
      >
        {/* Only the hovered artwork is mounted, so the six previews are not
            all downloaded on page load (the other sections share the files). */}
        {hover !== null && (
          <img
            key={ITEMS[hover].id}
            src={ITEMS[hover].img}
            alt=""
            style={{ position: "absolute", inset: 0 }}
          />
        )}
      </div>
    </section>
  );
};

export default ServicesV2;
